import React, { ReactElement } from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  SxProps,
} from "@mui/material";
import { TooltipLabel } from "../Tooltips";

export type RevbellSelectProps = SelectProps & {
  formControlSx?: SxProps;
  tooltip?: string;
  translateTooltip?: boolean;
  options: Array<{ label: string | ReactElement; value: string | number }>;
};

/**
 * Default select to use in revbell
 *
 * The `options` prop allows to generate a basic list of options.
 * @param props
 * @constructor
 */
export const RevbellSelect = (props: RevbellSelectProps) => {
  const {
    defaultValue,
    disabled,
    formControlSx,
    label,
    options,
    size,
    sx,
    tooltip,
    translateTooltip,
    ...rest
  } = props;

  const baseSx: SxProps = [
    {
      "&.MuiInputBase-root": {
        backgroundColor: "background.input",
        alignSelf: "center",
      },
      "& .MuiInput-underline:before": {
        content: "unset",
      },
      "& input": {
        fontSize: "0.875rem",
      },
      "& .MuiSelect-select": {
        fontSize: "0.875rem",
        lineHeight: "1.4375em",
      },
      "& .MuiSelect-icon, & .MuiIconButton-root": {
        color: "text.secondary",
      },
    },
    ...(Array.isArray(sx) ? sx : [sx]),
  ];

  const renderedSelect = (
    <Select
      {...rest}
      disabled={disabled}
      size={size}
      variant="outlined"
      sx={baseSx}
      defaultValue={defaultValue}
    >
      {options.map((item) => (
        <MenuItem key={item.value} value={item.value}>
          {item.label}
        </MenuItem>
      ))}
    </Select>
  );

  if (label) {
    return (
      <FormControl
        size={size}
        sx={{
          marginTop: 2,
          position: "relative",
          ...formControlSx,
        }}
      >
        {!tooltip && (
          <InputLabel
            sx={{
              fontSize: "0.8125rem",
              fontWeight: 700,
              transform: "translate(0px, -20px)",
            }}
          >
            {label}
          </InputLabel>
        )}
        {tooltip && (
          <TooltipLabel
            label={label}
            tooltipLabel={tooltip}
            translateTooltip={translateTooltip}
            sxStyles={{
              fontWeight: 700,
              color: disabled ? "text.disabled" : "text.secondary",
              transform: "translate(0px, -20px)",
              position: "absolute",
              top: 0,
              left: 0,
            }}
          />
        )}
        {renderedSelect}
      </FormControl>
    );
  }

  return renderedSelect;
};
